import { useEffect, useState } from "react"
import { BarChart, Bar, XAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { BarChartSkeleton } from "@/components/custom/skeletons"
import { ChevronLeft, ChevronRight, Check, HelpCircle, ChevronDown } from "lucide-react"
import { useCurrency } from "@/context/currency/currency-context"
import { useAuth } from "@/context/auth/auth-context"
import { type DividendEvent } from "@/types/portfolio"
import { aggregateDividendsByMonth, type MonthlyAggregate } from "@/lib/forecast-helper"
import hive from "@/lib/api/hive"

interface CustomTooltipProps {
	active?: boolean
	payload?: {
		dataKey: string
		value: number
		payload: MonthlyAggregate
	}[]
	formatCurrency: (val: number, options?: { fromCurrency?: string; skipConversion?: boolean }) => string
}

const CustomTooltip = ({ active, payload, formatCurrency }: CustomTooltipProps) => {
	if (active && payload && payload.length) {
		const monthData = payload[0].payload

		return (
			<div className="bg-background/95 backdrop-blur-sm border border-border p-3 rounded-lg shadow-lg text-sm max-w-md">
				<p className="font-bold text-sky-900 dark:text-sky-100 mb-2">{monthData.monthName}</p>

				{monthData.events.length === 0 ? (
					<p className="text-muted-foreground text-xs">No dividends this month</p>
				) : (
					<div className="space-y-1">
						{monthData.events.map((event, idx) => (
							<div key={idx} className="flex items-center justify-between gap-3 text-xs">
								<div className="flex items-center gap-1.5">
									{event.is_actual ? (
										<Check className="w-3 h-3 text-green-600" />
									) : (
										<HelpCircle className="w-3 h-3 text-blue-400" />
									)}
									<span className="font-medium">{event.ticker}</span>
								</div>
								<div className="text-right">
									<div>{formatCurrency(event.amount, { fromCurrency: event.currency })}</div>
									<div className="text-muted-foreground text-[10px]">
										{new Date(event.payment_date).toLocaleDateString("en-US", {
											month: "short",
											day: "numeric",
										})}
									</div>
								</div>
							</div>
						))}
						<div className="pt-1 mt-1 border-t border-border/50">
							<div className="flex justify-between text-xs font-medium">
								<span>Total:</span>
								<span>
									{formatCurrency(
										monthData.paid_total + monthData.declared_total + monthData.predicted_total,
										{ skipConversion: true },
									)}
								</span>
							</div>
						</div>
					</div>
				)}
			</div>
		)
	}
	return null
}

interface DividendBarChartProps {
	portfolioIds: string[]
	activeTickers?: string[]
	onEventsLoaded?: (events: DividendEvent[]) => void
	/** Pre-computed events (demo mode — skips API fetch when provided) */
	events?: DividendEvent[]
}

export function DividendBarChart({ portfolioIds, activeTickers, onEventsLoaded, events }: DividendBarChartProps) {
	const { format, convert } = useCurrency()
	const { token } = useAuth()
	const [viewYear, setViewYear] = useState(new Date().getFullYear())
	const [rawEvents, setRawEvents] = useState<DividendEvent[]>([])
	const [chartData, setChartData] = useState<MonthlyAggregate[]>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [touchStart, setTouchStart] = useState<number | null>(null)
	const [touchEnd, setTouchEnd] = useState<number | null>(null)
	const [swipeDirection, setSwipeDirection] = useState<"left" | "right" | null>(null)
	const [selectedMobileMonth, setSelectedMobileMonth] = useState<number | null>(null)

	// Minimum swipe distance (in px)
	const minSwipeDistance = 50

	// When events prop is provided (demo mode), use directly and skip API fetch
	useEffect(() => {
		if (events === undefined) return
		setRawEvents(events)
		onEventsLoaded?.(events)
		setLoading(false)
	}, [events])

	useEffect(() => {
		if (events !== undefined) return // skip API fetch when pre-computed events are provided
		const fetchForecast = async () => {
			if (!token || portfolioIds.length === 0) {
				setLoading(false)
				return
			}

			try {
				setLoading(true)
				setError(null)

				// Fetch forecasts for all portfolios
				const allEventsPromises = portfolioIds.map((id) => hive.fetchPortfolioForecast(id, token))
				const allEventsArrays = await Promise.all(allEventsPromises)

				// Flatten all events, filter out zero-amount and tickers not in holdings
				const fetched = allEventsArrays.flat().filter((event) => {
					if (event.amount <= 0) return false
					if (activeTickers && !activeTickers.includes(event.ticker)) return false
					return true
				})
				setRawEvents(fetched)
				onEventsLoaded?.(fetched)
			} catch (err) {
				setError(err instanceof Error ? err.message : "Failed to load forecast")
			} finally {
				setLoading(false)
			}
		}

		fetchForecast()
	}, [portfolioIds, token, activeTickers])

	useEffect(() => {
		const aggregated = aggregateDividendsByMonth(rawEvents, viewYear, convert)
		setChartData(aggregated)
	}, [rawEvents, viewYear, convert])

	const handlePrevYear = () => setViewYear((prev) => prev - 1)
	const handleNextYear = () => setViewYear((prev) => prev + 1)

	const onTouchStart = (e: React.TouchEvent) => {
		setTouchEnd(null)
		setTouchStart(e.targetTouches[0].clientX)
	}

	const onTouchMove = (e: React.TouchEvent) => {
		setTouchEnd(e.targetTouches[0].clientX)
	}

	const onTouchEnd = () => {
		if (!touchStart || !touchEnd) return

		const distance = touchStart - touchEnd
		const isLeftSwipe = distance > minSwipeDistance
		const isRightSwipe = distance < -minSwipeDistance

		if (isLeftSwipe) {
			setSwipeDirection("left")
			setTimeout(() => {
				handleNextYear()
				setSwipeDirection(null)
			}, 300)
		}
		if (isRightSwipe) {
			setSwipeDirection("right")
			setTimeout(() => {
				handlePrevYear()
				setSwipeDirection(null)
			}, 300)
		}
	}

	if (loading) {
		return (
			<Card className="h-full bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10 min-h-[400px]">
				<CardHeader>
					<div className="flex items-center justify-between">
						<Skeleton className="h-6 w-40" />
						<div className="flex items-center gap-2">
							<Skeleton className="h-8 w-8 rounded-md" />
							<Skeleton className="h-5 w-12" />
							<Skeleton className="h-8 w-8 rounded-md" />
						</div>
					</div>
				</CardHeader>
				<CardContent>
					<BarChartSkeleton />
				</CardContent>
			</Card>
		)
	}

	if (error) {
		return (
			<Card className="h-full bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10 flex items-center justify-center min-h-[400px]">
				<p className="text-red-600 dark:text-red-400">{error}</p>
			</Card>
		)
	}

	return (
		<Card className="h-full bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
			<CardHeader>
				<div className="flex items-center justify-between">
					<CardTitle className="text-sky-950 dark:text-sky-50">Dividend Forecast</CardTitle>
					<div className="flex items-center gap-2">
						<Button variant="outline" size="icon" onClick={handlePrevYear} className="h-8 w-8">
							<ChevronLeft className="h-4 w-4" />
						</Button>
						<span className="text-sm font-medium min-w-[60px] text-center">{viewYear}</span>
						<Button variant="outline" size="icon" onClick={handleNextYear} className="h-8 w-8">
							<ChevronRight className="h-4 w-4" />
						</Button>
					</div>
				</div>
			</CardHeader>
			<CardContent
				onTouchStart={onTouchStart}
				onTouchMove={onTouchMove}
				onTouchEnd={onTouchEnd}
				className={`transition-transform duration-300 ${
					swipeDirection === "left"
						? "-translate-x-4 opacity-70"
						: swipeDirection === "right"
							? "translate-x-4 opacity-70"
							: ""
				}`}
			>
				{/* Desktop Chart - Vertical Bars */}
				<div className="hidden md:block h-[280px] w-full [&_.recharts-wrapper]:outline-none [&_svg]:outline-none">
					<ResponsiveContainer width="100%" height="100%">
						<BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }} tabIndex={-1}>
							<CartesianGrid strokeDasharray="3 3" stroke="#0ea5e9" opacity={0.1} vertical={false} />
							<XAxis dataKey="monthName" height={0} tick={false} tickLine={false} axisLine={false} />
							<Tooltip
								content={<CustomTooltip formatCurrency={format} />}
								cursor={{ fill: "transparent", stroke: "none", strokeWidth: 0 }}
							/>
							<Bar
								dataKey="paid_total"
								stackId="a"
								fill="#0c4a6e"
								radius={[0, 0, 0, 0]}
								name="paid_total"
								activeBar={{ fill: "#0c4a6e", stroke: "none", strokeWidth: 0 }}
							/>
							<Bar
								dataKey="declared_total"
								stackId="a"
								fill="#0ea5e9"
								radius={[0, 0, 0, 0]}
								name="declared_total"
								activeBar={{ fill: "#0ea5e9", stroke: "none", strokeWidth: 0 }}
							/>
							<Bar
								dataKey="predicted_total"
								stackId="a"
								fill="#bae6fd"
								radius={[4, 4, 0, 0]}
								name="predicted_total"
								activeBar={{ fill: "#bae6fd", stroke: "none", strokeWidth: 0 }}
							/>
						</BarChart>
					</ResponsiveContainer>
				</div>

				{/* Mobile View - Month labels + Horizontal bars + tap to expand */}
				<div className="md:hidden w-full space-y-1">
					{chartData.map((month) => {
						const total = month.paid_total + month.declared_total + month.predicted_total
						const maxValue = Math.max(
							...chartData.map((m) => m.paid_total + m.declared_total + m.predicted_total),
						)
						const isExpanded = selectedMobileMonth === month.month

						return (
							<div key={month.month}>
								{/* Tappable row */}
								<div
									className="flex items-center gap-3 cursor-pointer py-1"
									onClick={() => setSelectedMobileMonth(isExpanded ? null : month.month)}
								>
									{/* Month label and amount */}
									<div className="flex items-center gap-1 w-24 shrink-0">
										<div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide min-w-[28px]">
											{month.monthName}
										</div>
										<div className="text-xs font-semibold text-sky-950 dark:text-sky-50 whitespace-nowrap">
											{total > 0 ? format(total, { skipConversion: true }) : "—"}
										</div>
									</div>

									{/* Horizontal stacked bar */}
									<div className="flex-1 h-6 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden flex">
										{month.paid_total > 0 && (
											<div
												className="bg-[#0c4a6e] h-full"
												style={{ width: `${(month.paid_total / maxValue) * 100}%` }}
											/>
										)}
										{month.declared_total > 0 && (
											<div
												className="bg-[#0ea5e9] h-full"
												style={{ width: `${(month.declared_total / maxValue) * 100}%` }}
											/>
										)}
										{month.predicted_total > 0 && (
											<div
												className="bg-[#bae6fd] h-full rounded-r"
												style={{ width: `${(month.predicted_total / maxValue) * 100}%` }}
											/>
										)}
									</div>

									{total > 0 && (
										<ChevronDown
											className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`}
										/>
									)}
								</div>

								{/* Expanded: per-company breakdown */}
								{isExpanded && month.events.length > 0 && (
									<div className="ml-[96px] mb-2 space-y-1 border-l-2 border-sky-200 dark:border-sky-800 pl-3">
										{month.events.map((event, idx) => (
											<div key={idx} className="flex items-center justify-between text-xs">
												<div className="flex items-center gap-1.5">
													{event.is_actual ? (
														<Check className="w-3 h-3 text-green-600 shrink-0" />
													) : (
														<HelpCircle className="w-3 h-3 text-blue-400 shrink-0" />
													)}
													<span className="font-medium">{event.ticker}</span>
													<span className="text-muted-foreground text-[10px]">
														{new Date(event.payment_date).toLocaleDateString("en-US", {
															month: "short",
															day: "numeric",
														})}
													</span>
												</div>
												<span className="font-semibold text-sky-950 dark:text-sky-50">
													{format(event.amount, { fromCurrency: event.currency })}
												</span>
											</div>
										))}
									</div>
								)}
							</div>
						)
					})}
				</div>

				{/* Desktop Monthly Breakdown */}
				<div className="hidden md:grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2 mt-2">
					{chartData.map((month) => {
						const total = month.paid_total + month.declared_total + month.predicted_total
						return (
							<div
								key={month.month}
								className="text-center space-y-0.5 py-1 px-1 rounded hover:bg-sky-50/50 dark:hover:bg-sky-900/10 transition-colors"
							>
								<div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
									{month.monthName}
								</div>
								<div className="text-sm font-semibold text-sky-950 dark:text-sky-50">
									{total > 0 ? format(total, { skipConversion: true }) : "—"}
								</div>
							</div>
						)
					})}
				</div>

				{/* Legend */}
				<div className="flex items-center justify-center gap-6 text-xs pt-4">
					<div className="flex items-center gap-2">
						<div className="w-3 h-3 rounded-sm bg-[#0c4a6e]"></div>
						<span className="text-muted-foreground">Paid</span>
					</div>
					<div className="flex items-center gap-2">
						<div className="w-3 h-3 rounded-sm bg-[#0ea5e9]"></div>
						<span className="text-muted-foreground">Declared</span>
					</div>
					<div className="flex items-center gap-2">
						<div className="w-3 h-3 rounded-sm bg-[#bae6fd]"></div>
						<span className="text-muted-foreground">Predicted</span>
					</div>
				</div>
			</CardContent>
		</Card>
	)
}
